import { Suspense } from "react";
import { Navigate, Outlet, Route } from "react-router";

import { PageLoading } from "shared/components/utils/PageLoading";
import { Permission } from "shared/types";
import { ShopProvider } from "shared/context/shop";
import { ShopSwitchReduxBridge } from "shared/components/navigation/ShopSelection";
import { UserProvider } from "shared/context/user/user-provider";
import {
  requireLegacyAdmin,
  requireLegacySales,
  requireLegacyUser,
  requireModernPermission
} from "./auth/auth-middleware";
import { ApplicationLayout } from "./application-layout";
import { ApplicationShell } from "./application-shell";
import { PublicLayout } from "./public-layout";
import { FeatureBoundary } from "./boundaries/feature-boundary";
import { LegacyDashboardLayout } from "./boundaries/legacy-dashboard-layout";
import { GlobalLegacyLayout } from "./boundaries/global-legacy-layout";
import { LegacyLoginLayout } from "./boundaries/legacy-login-layout";
import { LegacyRouteLoadingBoundary } from "./boundaries/legacy-route-loading-boundary";
import { lazyDefaultPage } from "./lazy-default-page";
import { LegacyAuthRedirectBoundary } from "./redirects/legacy-auth-redirect-boundary";
import { LocationPreservingRedirect } from "./redirects/location-preserving-redirect";
import { RootRedirect } from "./redirects/root-redirect";
import { RouteErrorBoundary } from "./route-error-boundary";
import { to } from "./to";

const RouteLoadingBoundary = () => (
  <Suspense fallback={<PageLoading />}>
    <Outlet />
  </Suspense>
);

const applicationRoutes = (
  <Route
    element={<ApplicationShell />}
    errorElement={<RouteErrorBoundary />}
    HydrateFallback={PageLoading}
  >
    <Route element={<RouteLoadingBoundary />}>
      <Route element={<PublicLayout />}>
        <Route
          path="/login"
          lazy={lazyDefaultPage(() => import("modules/Auth/pages/Login.page"))}
        />
        <Route
          path="/register"
          lazy={lazyDefaultPage(() => import("modules/Auth/pages/Register.page"))}
        />
        <Route
          path="/password-reset"
          lazy={lazyDefaultPage(() => import("modules/Auth/pages/ResetPassword.page"))}
        />
        <Route
          path="/set-password/:key"
          lazy={lazyDefaultPage(() => import("modules/Auth/pages/SetPassword.page"))}
        />
        <Route
          path="/create-account/:key"
          lazy={lazyDefaultPage(() => import("modules/Auth/pages/CreateAccount.page"))}
        />
      </Route>

      <Route element={<FeatureBoundary flag="maintenance_screen_enabled" />}>
        <Route
          path="/maintenance"
          lazy={lazyDefaultPage(() => import("modules/Internal/pages/Maintenance.page"))}
        />
      </Route>

      <Route element={<PublicLayout />}>
        <Route element={<LegacyLoginLayout />}>
          <Route element={<LegacyRouteLoadingBoundary loadingState="login" />}>
            <Route
              path="/create-account/success"
              lazy={lazyDefaultPage(
                () => import("legacy/containers/CustomerLogin/Containers/AccountCreationSuccessful")
              )}
            />
          </Route>
        </Route>
      </Route>

      <Route element={<UserProvider />}>
        <Route middleware={[requireModernPermission(Permission.USER)]}>
          <Route
            element={
              <ShopProvider>
                <ShopSwitchReduxBridge>
                  <Outlet />
                </ShopSwitchReduxBridge>
              </ShopProvider>
            }
          >
            <Route element={<ApplicationLayout withShopNavigation />}>
              <Route
                path="/shop/:shopId/dashboard"
                lazy={lazyDefaultPage(() => import("modules/Dashboard/pages/Dashboard.page"))}
              />
              <Route
                path="/shop/:shopId/features/browse-abandonment"
                lazy={lazyDefaultPage(
                  () => import("modules/Features/pages/BrowseAbandonment.page")
                )}
              />
              <Route
                path="/shop/:shopId/features/come-back-mailing"
                lazy={lazyDefaultPage(() => import("modules/Features/pages/ComeBackMailing.page"))}
              />
              <Route
                path="/shop/:shopId/account/onboarding-questionnaire"
                lazy={lazyDefaultPage(
                  () => import("modules/Questionnaire/pages/Questionnaire.page")
                )}
              />
              <Route
                path="/shop/:shopId/contact-persons"
                lazy={lazyDefaultPage(() => import("modules/Settings/pages/ContactPersons.page"))}
              />
              <Route
                path="/shop/:shopId/shop-links"
                lazy={lazyDefaultPage(() => import("../pages/shop/shop-links.page"))}
              />
            </Route>
          </Route>
        </Route>

        <Route middleware={[requireModernPermission(Permission.SALES)]}>
          <Route
            element={
              <ShopProvider>
                <ShopSwitchReduxBridge>
                  <Outlet />
                </ShopSwitchReduxBridge>
              </ShopProvider>
            }
          >
            <Route element={<ApplicationLayout withShopNavigation />}>
              <Route
                path="/shop/:shopId/products/trigger-mails-payment-settings"
                lazy={lazyDefaultPage(
                  () => import("modules/Settings/pages/TriggerMailsPayment.page")
                )}
              />
            </Route>
          </Route>
          <Route element={<ApplicationLayout />}>
            <Route
              path="/admin/trigger-mails-block"
              lazy={lazyDefaultPage(
                () => import("modules/Administration/pages/TriggerMailsBlock.page")
              )}
            />
          </Route>
        </Route>

        <Route errorElement={<LegacyAuthRedirectBoundary />} middleware={[requireLegacyUser]}>
          <Route element={<PublicLayout />}>
            <Route element={<LegacyLoginLayout />}>
              <Route element={<LegacyRouteLoadingBoundary loadingState="login" />}>
                <Route
                  path="/create-account/onboarding"
                  lazy={lazyDefaultPage(() => import("legacy/containers/OnBoarding/Welcome"))}
                />
              </Route>
            </Route>
          </Route>

          <Route element={<ApplicationLayout />}>
            <Route element={<GlobalLegacyLayout />}>
              <Route element={<LegacyRouteLoadingBoundary loadingState="dashboard" />}>
                <Route
                  path="/dashboard-old"
                  lazy={lazyDefaultPage(() => import("legacy/containers/LiveDashboard"))}
                />
              </Route>
            </Route>
          </Route>
          <Route
            element={
              <ShopProvider>
                <ShopSwitchReduxBridge>
                  <Outlet />
                </ShopSwitchReduxBridge>
              </ShopProvider>
            }
          >
            <Route element={<ApplicationLayout withShopNavigation />}>
              <Route element={<LegacyDashboardLayout />}>
                <Route element={<LegacyRouteLoadingBoundary loadingState="dashboard" />}>
                  <Route
                    path="/shop/:shopId/features/activation-popups"
                    lazy={lazyDefaultPage(
                      () => import("legacy/containers/ProductSettings/ActivationSettings")
                    )}
                  />
                  <Route
                    path="/shop/:shopId/features/abandonment-mails"
                    lazy={lazyDefaultPage(() => import("pages/shop/abandonment-mails.page"))}
                  />
                  <Route
                    path="/shop/:shopId/features/newsletter-forms"
                    lazy={lazyDefaultPage(
                      () => import("legacy/containers/ProductSettings/OptinSettings")
                    )}
                  />
                  <Route
                    path="/shop/:shopId/integrations"
                    lazy={lazyDefaultPage(() => import("legacy/containers/IntegrationSettings"))}
                  />
                  <Route
                    path="/shop/:shopId/algorithm"
                    lazy={lazyDefaultPage(() => import("legacy/containers/CustomerSettings"))}
                  />
                  <Route
                    path="/shop/:shopId/account/my-account"
                    lazy={lazyDefaultPage(() => import("legacy/containers/Settings"))}
                  />
                  <Route
                    path="/shop/:shopId/products/tracking"
                    lazy={lazyDefaultPage(() => import("legacy/containers/ShopSettings/Tracking"))}
                  />
                  <Route
                    path="/shop/:shopId/products/shop-configuration"
                    lazy={lazyDefaultPage(() => import("legacy/containers/ShopConfiguration"))}
                  />
                </Route>
              </Route>
            </Route>
          </Route>
          <Route element={<ApplicationLayout />}>
            <Route element={<GlobalLegacyLayout />}>
              <Route element={<LegacyRouteLoadingBoundary loadingState="dashboard" />}>
                <Route
                  path="/admin/reported-regains"
                  lazy={lazyDefaultPage(
                    () => import("legacy/containers/Admin/ReportedData/ReportedRegains")
                  )}
                />
                <Route
                  path="/admin/reported-subscriptions"
                  lazy={lazyDefaultPage(
                    () => import("legacy/containers/Admin/ReportedData/ReportedSubscriptions")
                  )}
                />
                <Route
                  path="/admin/store-administration"
                  lazy={lazyDefaultPage(
                    () => import("legacy/containers/Admin/StoreAdministration")
                  )}
                />
                <Route
                  path="/admin/user-administration"
                  lazy={lazyDefaultPage(() => import("legacy/containers/Admin/UserAdministration"))}
                />
                <Route
                  path="/admin/feature-toggles"
                  lazy={lazyDefaultPage(() => import("legacy/containers/Admin/FeatureToggles"))}
                />
                <Route
                  path="/admin/test-pricing-form"
                  lazy={lazyDefaultPage(() => import("legacy/containers/Admin/TestPricingForm"))}
                />
                <Route
                  path="/admin/sales/provision"
                  lazy={lazyDefaultPage(() => import("legacy/containers/Sales/Dashboard"))}
                />
              </Route>
            </Route>
          </Route>
        </Route>

        <Route errorElement={<LegacyAuthRedirectBoundary />} middleware={[requireLegacySales]}>
          <Route element={<ApplicationLayout />}>
            <Route element={<GlobalLegacyLayout />}>
              <Route element={<LegacyRouteLoadingBoundary loadingState="dashboard" />}>
                <Route
                  path="/admin/analysis/mainCancellationDrops"
                  lazy={lazyDefaultPage(
                    () => import("legacy/containers/Analysis/Drops/MailCancellationDrops")
                  )}
                />
                <Route
                  path="/admin/analysis/activationCancellationDrops"
                  lazy={lazyDefaultPage(
                    () => import("legacy/containers/Analysis/Drops/ActivationCancellationDrops")
                  )}
                />
                <Route
                  path="/admin/analysis/activationRegainDrops"
                  lazy={lazyDefaultPage(
                    () => import("legacy/containers/Analysis/Drops/ActivationRegainDrops")
                  )}
                />
                <Route
                  path="/admin/analysis/mailRegainDrops"
                  lazy={lazyDefaultPage(
                    () => import("legacy/containers/Analysis/Drops/MailRegainDrops")
                  )}
                />
                <Route
                  path="/admin/analysis/sessionDrops"
                  lazy={lazyDefaultPage(
                    () => import("legacy/containers/Analysis/Drops/SessionDrops")
                  )}
                />
                <Route
                  path="/admin/analysis/orderDrops"
                  lazy={lazyDefaultPage(
                    () => import("legacy/containers/Analysis/Drops/OrderDrops")
                  )}
                />
                <Route
                  path="/admin/analysis/zeroScv"
                  lazy={lazyDefaultPage(
                    () => import("legacy/containers/Analysis/Irregular/ZeroScv")
                  )}
                />
                <Route
                  path="/admin/analysis/invoiceDrops"
                  lazy={lazyDefaultPage(() => import("legacy/containers/Analysis/Drops/Invoices"))}
                />
                <Route
                  path="/admin/analysis/invoices"
                  lazy={lazyDefaultPage(
                    () => import("legacy/containers/Analysis/Invoices/Overview")
                  )}
                />
              </Route>
            </Route>
          </Route>
        </Route>

        <Route errorElement={<LegacyAuthRedirectBoundary />} middleware={[requireLegacyAdmin]}>
          <Route
            element={
              <ShopProvider>
                <ShopSwitchReduxBridge>
                  <Outlet />
                </ShopSwitchReduxBridge>
              </ShopProvider>
            }
          >
            <Route element={<ApplicationLayout withShopNavigation />}>
              <Route element={<LegacyDashboardLayout />}>
                <Route element={<LegacyRouteLoadingBoundary loadingState="dashboard" />}>
                  <Route
                    path="/shop/:shopId/activationDesigner"
                    lazy={lazyDefaultPage(() => import("legacy/containers/ActivationDesigner"))}
                  />
                </Route>
              </Route>
            </Route>
          </Route>
          <Route element={<ApplicationLayout />}>
            <Route element={<GlobalLegacyLayout />}>
              <Route element={<LegacyRouteLoadingBoundary loadingState="dashboard" />}>
                <Route
                  path="/admin/activationManager"
                  lazy={lazyDefaultPage(() => import("legacy/containers/ActivationManager"))}
                />
                <Route
                  path="/admin/editor/content"
                  lazy={lazyDefaultPage(() => import("legacy/containers/Cms/Editor/app"))}
                />
                <Route
                  path="/admin/template-editor"
                  lazy={lazyDefaultPage(() => import("legacy/containers/TemplateEditor"))}
                />
              </Route>
            </Route>
          </Route>
          <Route
            element={
              <ShopProvider>
                <ShopSwitchReduxBridge>
                  <Outlet />
                </ShopSwitchReduxBridge>
              </ShopProvider>
            }
          >
            <Route element={<ApplicationLayout withShopNavigation />}>
              <Route element={<LegacyDashboardLayout />}>
                <Route element={<LegacyRouteLoadingBoundary loadingState="dashboard" />}>
                  <Route
                    path="/shop/:shopId/abtests"
                    lazy={lazyDefaultPage(() => import("legacy/containers/ABTestDesigner"))}
                  />
                </Route>
              </Route>
            </Route>
          </Route>
          <Route element={<ApplicationLayout />}>
            <Route element={<GlobalLegacyLayout />}>
              <Route element={<LegacyRouteLoadingBoundary loadingState="dashboard" />}>
                <Route
                  path="/admin/mailTemplates"
                  lazy={lazyDefaultPage(() => import("legacy/containers/MailTemplateDesigner"))}
                />
              </Route>
            </Route>
          </Route>
          <Route
            element={
              <ShopProvider>
                <ShopSwitchReduxBridge>
                  <Outlet />
                </ShopSwitchReduxBridge>
              </ShopProvider>
            }
          >
            <Route element={<ApplicationLayout withShopNavigation />}>
              <Route element={<LegacyDashboardLayout />}>
                <Route element={<LegacyRouteLoadingBoundary loadingState="dashboard" />}>
                  <Route
                    path="/shop/:shopId/fuzzyEditor"
                    lazy={lazyDefaultPage(() => import("legacy/containers/FuzzyEditor"))}
                  />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>

      <Route
        path="/activationManager"
        element={<LocationPreservingRedirect pathname={to.activationManager} />}
      />
      <Route
        path="/mailTemplates"
        element={<LocationPreservingRedirect pathname={to.mailTemplates} />}
      />
      <Route
        path="/sales/provision"
        element={<LocationPreservingRedirect pathname={to.provisionDashboard} />}
      />
      <Route
        path="/analysis/mainCancellationDrops"
        element={<LocationPreservingRedirect pathname={to.mailCancellationDrops} />}
      />
      <Route
        path="/analysis/activationCancellationDrops"
        element={<LocationPreservingRedirect pathname={to.activationCancellationDrops} />}
      />
      <Route
        path="/analysis/activationRegainDrops"
        element={<LocationPreservingRedirect pathname={to.activationRegainDrops} />}
      />
      <Route
        path="/analysis/mailRegainDrops"
        element={<LocationPreservingRedirect pathname={to.mailRegainDrops} />}
      />
      <Route
        path="/analysis/sessionDrops"
        element={<LocationPreservingRedirect pathname={to.sessionDrops} />}
      />
      <Route
        path="/analysis/orderDrops"
        element={<LocationPreservingRedirect pathname={to.orderDrops} />}
      />
      <Route
        path="/analysis/zeroScv"
        element={<LocationPreservingRedirect pathname={to.zeroScv} />}
      />
      <Route
        path="/analysis/invoiceDrops"
        element={<LocationPreservingRedirect pathname={to.invoiceDrops} />}
      />
      <Route
        path="/analysis/invoices"
        element={<LocationPreservingRedirect pathname={to.invoices} />}
      />
      <Route path="/" element={<RootRedirect />} />
      <Route element={<PublicLayout />}>
        <Route element={<LegacyRouteLoadingBoundary loadingState="dashboard" />}>
          <Route path="*" element={<Navigate to={to.root} replace />} />
        </Route>
      </Route>
    </Route>
  </Route>
);

export { applicationRoutes };
